import { BAY_HIRE_QUERY_KEYS } from "@features/bay-hire/constants";
import {
  bayHireService,
  type AddToWaitingListRequest,
  type ApproveExemptedRequest,
  type AttachmentUploadRequest,
  type BayHireRequestCreate,
  type CancelRequest,
  type PreApproveRequest,
  type RejectRequest,
  type ResubmitRequest,
  type UploadReceiptRequest,
  type VendorUpdateRequest,
} from "@services/bayHireApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

// DFT-0118: all TRANSACTION-classified hooks below wrap mutationFn with runTransaction, using the
// established codebase idiom (see useClaimVesselRegistrationRequest.tsx). onSuccess/invalidation
// logic is untouched. screen is omitted for hooks whose owning dialog renders from more than one
// screen (useCancelBayHireRequest, useUploadBayHireReceipt) - see plan.md Decision 4.

export const useStartBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: BayHireRequestCreate) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestForm",
          action: "submit_bay_hire_request",
          entityType: "bay_hire_request",
        },
        (transactionId) => bayHireService.startRequest(request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.requests] });
    },
  });
};

export const useResubmitBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ResubmitRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestForm",
          action: "resubmit_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.resubmitRequest(id, request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.requests] });
    },
  });
};

// DFT-0044 slice V (F1): the request's own detail cache key is invalidated too, not just the
// list - a vendor who updates from the list, then navigates straight to the detail route, must
// not see the pre-update snapshot the detail query already cached.
export const useUpdateBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: VendorUpdateRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestForm",
          action: "update_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.updateRequest(id, request, transactionId),
      ),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.requests] });
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.request, id] });
    },
  });
};

export const useCancelBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: CancelRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          action: "cancel_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.cancelRequest(id, request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.requests] });
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

export const useRejectBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: RejectRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "reject_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.rejectRequest(id, request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

// DFT-0015: Operations manually defers a claimed REQUESTED request to the waiting list.
export const useAddToWaitingListBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: AddToWaitingListRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "add_to_waiting_list_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.addToWaitingList(id, request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

export const useClaimBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "claim_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.claim(id, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

export const useUnclaimBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "unclaim_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.unclaim(id, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

export const usePreApproveBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: PreApproveRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "pre_approve_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.preApprove(id, request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

// DFT-0084: direct-approve an EXEMPTED request - same invalidation as usePreApproveBayHireRequest.
export const useApproveExemptedBayHireRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ApproveExemptedRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "approve_exempted_bay_hire_request",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.approveExempted(id, request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

// DFT-0103: the eager `useUploadBayHireAttachment` hook (POST .../attachments/upload, wrapping
// bayHireService.uploadAttachmentFile) that used to back Cancel/Reject/Pre-approve/Add Receipt's
// upload UI has been REMOVED - all four surfaces durably persisted an attachment the instant a
// file was dropped, before the drawer's own submit action was ever clicked, leaving an orphaned
// (or, for Add Receipt, orphaned-and-invisible) row behind whenever the drawer was abandoned. They
// now stage the blob only (`bayHireService.uploadStagedAttachment`, no backend attach call) on
// drop and register it durably via `useUploadBayHireAttachmentMetadata` below, at submit time only -
// mirroring the pattern `useBayHireContainer.tsx`'s `handleUpdateFormSubmit`/
// `handleResubmitFormSubmit` already use for Update/Resubmit's HOD attachment. See task DFT-0103
// and `BayHireCreateAttachmentUpload.tsx`'s own class doc for the full rationale.

// DFT-0118: NO_TELEMETRY_NEEDED - re-verified this session (call sites: BayHireRequestFormPage.tsx,
// useReceiptUploadDialog.tsx, usePreApproveDialog.tsx, ReasonDialog.tsx). This hook is always
// invoked as an internal sub-step of a larger create/update/resubmit/pre-approve/reject/cancel/
// receipt-upload operation, never independently by a user - instrumenting it separately would
// produce a spurious nested transaction for what is really a sub-step of an already-instrumented
// (or about-to-be-instrumented) operation. See plan.md Technical Approach.
export const useUploadBayHireAttachmentMetadata = () => {
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: AttachmentUploadRequest }) =>
      bayHireService.uploadAttachment(id, request),
  });
};

// DFT-0014: Vendor uploads a payment receipt for a chargeable, pre-approved request.
export const useUploadBayHireReceipt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UploadReceiptRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          action: "upload_bay_hire_receipt",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.uploadReceipt(id, request, transactionId),
      ),
    // 🔴 Bug fix (2026-08-05): only invalidated the LIST key - the Detail page's own `[request,
    // id]` query (where the uploaded receipt is actually displayed, in the Documents tab) was left
    // stale. Same class of gap as useUploadBayHireAttachment's own fix, above.
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.requests] });
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.request, id] });
    },
  });
};

// DFT-0014: Operations verifies a submitted payment receipt (no reason).
export const useVerifyBayHirePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "verify_bay_hire_payment",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.verifyPayment(id, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};

// DFT-0014: Operations rejects a submitted payment receipt with a mandatory reason.
export const useRejectBayHirePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: RejectRequest }) =>
      runTransaction(
        {
          module: "bay_hire",
          screen: "BayHireRequestDetail",
          action: "reject_bay_hire_payment",
          entityType: "bay_hire_request",
          entityId: id,
        },
        (transactionId) => bayHireService.rejectPayment(id, request, transactionId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BAY_HIRE_QUERY_KEYS.queue] });
    },
  });
};
